import config from '../../../../config.json';
import { useRef, useMemo } from 'react';
import Button from 'components/v4/Button';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import walkthroughIds from '../walkthroughIds';
import AGGridMain from '@/components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../../hooks/useColumnState';
import { useQuery } from 'hooks/useCustomQuery';
import { axios } from '@/services/axios';
import { HStack, IconButton, Tooltip } from '@chakra-ui/react';
import { StatusCellRenderer } from 'components/index';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import { BsThreeDotsVertical } from 'react-icons/bs';

const APIUsers = () => {
    const navigate = useNavigate();
    const tableRef = useRef();

    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { darkMode } = useSelector((state) => state.settings);
    const baseColumns = useMemo(
        () => [
            { headerName: 'Name', field: 'name', sortable: true },
            {
                headerName: 'API Key',
                field: 'apiKey',
                sortable: true,
                cellRenderer: ({ value }) => value + '...',
            },
            {
                headerName: 'Permission',
                field: 'permissionSet',
                sortable: true,
            },
            {
                headerName: 'Last Logged In',
                field: 'lastLoggedIn',
                sortable: true,
                valueFormatter: (params) => {
                    if (!params.value) return '';
                    if (params.value.startsWith('0001-01-01')) return 'Never';
                    const date = new Date(params.value);
                    if (isNaN(date.getTime())) return params.value;
                    // Format: Fri, 26 Feb 2021, 11:42:11 pm UTC
                    const dayName = date.toLocaleString('en-GB', {
                        weekday: 'short',
                        timeZone: 'UTC',
                    });
                    const day = date.toLocaleString('en-GB', {
                        day: '2-digit',
                        timeZone: 'UTC',
                    });
                    const month = date.toLocaleString('en-GB', {
                        month: 'short',
                        timeZone: 'UTC',
                    });
                    const year = date.toLocaleString('en-GB', {
                        year: 'numeric',
                        timeZone: 'UTC',
                    });
                    let hour = date.getUTCHours();
                    let minute = date.getUTCMinutes();
                    let second = date.getUTCSeconds();
                    let hour12 = hour % 12 || 12;
                    let ampm = hour < 12 ? 'am' : 'pm';
                    const pad = (n) => n.toString().padStart(2, '0');
                    return `${dayName}, ${pad(day)} ${month} ${year}, ${pad(hour12)}:${pad(minute)}:${pad(second)} ${ampm} UTC`;
                },
                cellRenderer: (params) => {
                    if (params.value && params.value.startsWith('0001-01-01')) {
                        return CopyCellRenderer({
                            value: params.value,
                            valueFormatted: 'Never',
                        });
                    }
                    // Show formatted value, but copy raw value
                    const date = new Date(params.value);
                    if (isNaN(date.getTime())) return params.value;
                    // Format: Fri, 26 Feb 2021, 11:42:11 pm UTC
                    const dayName = date.toLocaleString('en-GB', {
                        weekday: 'short',
                        timeZone: 'UTC',
                    });
                    const day = date.toLocaleString('en-GB', {
                        day: '2-digit',
                        timeZone: 'UTC',
                    });
                    const month = date.toLocaleString('en-GB', {
                        month: 'short',
                        timeZone: 'UTC',
                    });
                    const year = date.toLocaleString('en-GB', {
                        year: 'numeric',
                        timeZone: 'UTC',
                    });
                    let hour = date.getUTCHours();
                    let minute = date.getUTCMinutes();
                    let second = date.getUTCSeconds();
                    let hour12 = hour % 12 || 12;
                    let ampm = hour < 12 ? 'am' : 'pm';
                    const pad = (n) => n.toString().padStart(2, '0');
                    const formatted = `${dayName}, ${pad(day)} ${month} ${year}, ${pad(hour12)}:${pad(minute)}:${pad(second)} ${ampm} UTC`;
                    return CopyCellRenderer({
                        value: params.data.dateTime,
                        valueFormatted: formatted,
                    });
                },
                filterParams: {
                    valueFormatter: (params) => {
                        if (!params.value) return '';
                        if (params.value.startsWith('0001-01-01'))
                            return 'Never';
                        const date = new Date(params.value);
                        if (isNaN(date.getTime())) return params.value;
                        // Format: Fri, 26 Feb 2021, 11:42:11 pm UTC
                        const dayName = date.toLocaleString('en-GB', {
                            weekday: 'short',
                            timeZone: 'UTC',
                        });
                        const day = date.toLocaleString('en-GB', {
                            day: '2-digit',
                            timeZone: 'UTC',
                        });
                        const month = date.toLocaleString('en-GB', {
                            month: 'short',
                            timeZone: 'UTC',
                        });
                        const year = date.toLocaleString('en-GB', {
                            year: 'numeric',
                            timeZone: 'UTC',
                        });
                        let hour = date.getUTCHours();
                        let minute = date.getUTCMinutes();
                        let second = date.getUTCSeconds();
                        let hour12 = hour % 12 || 12;
                        let ampm = hour < 12 ? 'am' : 'pm';
                        const pad = (n) => n.toString().padStart(2, '0');
                        return `${dayName}, ${pad(day)} ${month} ${year}, ${pad(hour12)}:${pad(minute)}:${pad(second)} ${ampm} UTC`;
                    },
                },
            },
            {
                headerName: 'Expiration',
                field: 'expiration',
                sortable: true,
                valueFormatter: (params) => {
                    if (!params.value) return '';
                    if (params.value.startsWith('0001-01-01')) return 'Never';
                    const date = new Date(params.value);
                    if (isNaN(date.getTime())) return params.value;
                    // Format: Fri, 26 Feb 2021, 11:42:11 pm UTC
                    const dayName = date.toLocaleString('en-GB', {
                        weekday: 'short',
                        timeZone: 'UTC',
                    });
                    const day = date.toLocaleString('en-GB', {
                        day: '2-digit',
                        timeZone: 'UTC',
                    });
                    const month = date.toLocaleString('en-GB', {
                        month: 'short',
                        timeZone: 'UTC',
                    });
                    const year = date.toLocaleString('en-GB', {
                        year: 'numeric',
                        timeZone: 'UTC',
                    });
                    let hour = date.getUTCHours();
                    let minute = date.getUTCMinutes();
                    let second = date.getUTCSeconds();
                    let hour12 = hour % 12 || 12;
                    let ampm = hour < 12 ? 'am' : 'pm';
                    const pad = (n) => n.toString().padStart(2, '0');
                    return `${dayName}, ${pad(day)} ${month} ${year}, ${pad(hour12)}:${pad(minute)}:${pad(second)} ${ampm} UTC`;
                },
                cellRenderer: (params) => {
                    if (params.value && params.value.startsWith('0001-01-01')) {
                        return CopyCellRenderer({
                            value: params.value,
                            valueFormatted: 'Never',
                        });
                    }
                    // Show formatted value, but copy raw value
                    const date = new Date(params.value);
                    if (isNaN(date.getTime())) return params.value;
                    // Format: Fri, 26 Feb 2021, 11:42:11 pm UTC
                    const dayName = date.toLocaleString('en-GB', {
                        weekday: 'short',
                        timeZone: 'UTC',
                    });
                    const day = date.toLocaleString('en-GB', {
                        day: '2-digit',
                        timeZone: 'UTC',
                    });
                    const month = date.toLocaleString('en-GB', {
                        month: 'short',
                        timeZone: 'UTC',
                    });
                    const year = date.toLocaleString('en-GB', {
                        year: 'numeric',
                        timeZone: 'UTC',
                    });
                    let hour = date.getUTCHours();
                    let minute = date.getUTCMinutes();
                    let second = date.getUTCSeconds();
                    let hour12 = hour % 12 || 12;
                    let ampm = hour < 12 ? 'am' : 'pm';
                    const pad = (n) => n.toString().padStart(2, '0');
                    const formatted = `${dayName}, ${pad(day)} ${month} ${year}, ${pad(hour12)}:${pad(minute)}:${pad(second)} ${ampm} UTC`;
                    return CopyCellRenderer({
                        value: params.data.dateTime,
                        valueFormatted: formatted,
                    });
                },
                filterParams: {
                    valueFormatter: (params) => {
                        if (!params.value) return '';
                        if (params.value.startsWith('0001-01-01'))
                            return 'Never';
                        const date = new Date(params.value);
                        if (isNaN(date.getTime())) return params.value;
                        // Format: Fri, 26 Feb 2021, 11:42:11 pm UTC
                        const dayName = date.toLocaleString('en-GB', {
                            weekday: 'short',
                            timeZone: 'UTC',
                        });
                        const day = date.toLocaleString('en-GB', {
                            day: '2-digit',
                            timeZone: 'UTC',
                        });
                        const month = date.toLocaleString('en-GB', {
                            month: 'short',
                            timeZone: 'UTC',
                        });
                        const year = date.toLocaleString('en-GB', {
                            year: 'numeric',
                            timeZone: 'UTC',
                        });
                        let hour = date.getUTCHours();
                        let minute = date.getUTCMinutes();
                        let second = date.getUTCSeconds();
                        let hour12 = hour % 12 || 12;
                        let ampm = hour < 12 ? 'am' : 'pm';
                        const pad = (n) => n.toString().padStart(2, '0');
                        return `${dayName}, ${pad(day)} ${month} ${year}, ${pad(hour12)}:${pad(minute)}:${pad(second)} ${ampm} UTC`;
                    },
                },
            },
            {
                headerName: 'Active',
                field: 'isActive',
                width: 100,
                valueGetter: (params) =>
                    params.data?.isActive ? 'Active' : 'Inactive',
                cellRenderer: StatusCellRenderer,
                cellRendererParams: (params) => ({
                    value: params.data?.isActive,
                }),
            },
            {
                lockPosition: 'right',
                headerName: 'Action',
                maxWidth: 100,
                width: 120,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                filter: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: function ActionsRenderer(params) {
                    const rowData = params.data;
                    return (
                        <Tooltip label="Edit Api User">
                            <IconButton
                                icon={
                                    <BsThreeDotsVertical
                                        fill={darkMode ? 'white' : 'black'}
                                    />
                                }
                                aria-label="Edit"
                                variant="ghost"
                                data-walkthroughid={`${walkthroughIds.ufTableApiUserEditBtn}/${rowData?.name}`}
                                onClick={() =>
                                    navigate(`/admins/api/${rowData.id}/edit`, {
                                        state: rowData,
                                    })
                                }
                                _hover={{
                                    bg: darkMode
                                        ? 'whiteAlpha.200'
                                        : 'blackAlpha.100',
                                }}
                                _active={{
                                    bg: darkMode
                                        ? 'whiteAlpha.300'
                                        : 'blackAlpha.200',
                                }}
                                _focus={{ boxShadow: 'none' }}
                            />
                        </Tooltip>
                    );
                },
            },
        ],
        [darkMode, navigate],
    );

    const { columns: agGridColumns, onColumnVisible } =
        useColumnState(baseColumns);

    // Data fetching with TanStack useQuery
    const {
        data: apiUsers = [],
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ['apiUsers'],
        queryFn: async () => {
            const res = await axios.get(
                config.apiUsers.tableFetch + currentCompany,
            );
            return res.data || [];
        },
        enabled: Boolean(currentCompany),
        keepPreviousData: true,
    });

    const tableDataWithColumns = useMemo(
        () => ({
            columns: agGridColumns,
            data: apiUsers,
        }),
        [agGridColumns, apiUsers],
    );

    return (
        <>
            <AGGridMain
                gridRef={tableRef}
                tableData={tableDataWithColumns}
                titleName={config.apiUsers.tableTitle}
                isLoading={isLoading}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                extraComponents={
                    <HStack spacing={2} paddingLeft={5}>
                        <Tooltip
                            label="Add API User"
                            placement="bottom"
                            hasArrow
                            portalProps={{ containerRef: undefined }}>
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                }}>
                                <Button
                                    size="sm"
                                    width="90px"
                                    onClick={() => navigate('/admins/api')}>
                                    + API User
                                </Button>
                            </div>
                        </Tooltip>
                    </HStack>
                }
            />
        </>
    );
};

export default APIUsers;
